import { useTranslation } from 'react-i18next';
import { Box, Button, Pagination, Tooltip, styled } from '@mui/material';
import { lighten } from '@mui/material';
import { ControlledAutoComplete } from 'components/shared/ControlledFields';
import { enumToOptions } from '../utilts';
import { DARK, WHITE } from 'constants/colors';
import { Control } from 'react-hook-form';
import {
  MessageActionType,
  MessageForm,
  TaskDefinitionGroup,
  TaskDefinitionStatus
} from '../types';
import useUpdateEmployeeMessageTaskBulk from '../hooks/useUpdateEmployeeMessageTaskBulk';
import { useQueryClient } from 'react-query';
import { Icon } from '@sdb/planning-ui';

const Header = styled('header')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: -20px;
  z-index: 1;

  padding: 16px;
  background-color: ${WHITE};
  border-bottom: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const FilterContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 16px;

  .MuiTextField-root {
    width: 200px;
  }
`;

const SyncButton = styled(Button)`
  min-width: 36px;
  height: 36px;
  box-sizing: border-box;
  color: white;
  border-radius: 4px;
  padding: 8px;

  &:hover {
    background-color: ${lighten(DARK, 0.2)};
  }
`;

type FilterProps = {
  control: Control<MessageForm>;
  onPageChange: (pageNr: number) => void;
  pageCount: number;
  refetchMessages: () => void;
  selectedMessages: string[];
  allMessagesSelected?: boolean;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  enableBulkActions?: boolean;
  isEmployeeCard?: boolean;
};

const Filters = (props: FilterProps) => {
  const {
    control,
    onPageChange,
    pageCount,
    refetchMessages,
    selectedMessages,
    allMessagesSelected = false,
    onSelectAll,
    onDeselectAll,
    enableBulkActions = false,
    isEmployeeCard = false
  } = props;
  const { t } = useTranslation('common');

  const { mutateAsync: updateAction } = useUpdateEmployeeMessageTaskBulk();
  const queryClient = useQueryClient();

  const handleAction = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    await updateAction({
      taskIds: selectedMessages,
      actionType: MessageActionType.SEEN
    });

    onDeselectAll();
    refetchMessages();

    queryClient.invalidateQueries('employeeMessages');
  };

  const renderAutoComplete = (name: string, type: any) => (
    <ControlledAutoComplete
      disableSorting
      optionLabelProp="label"
      idProperty="value"
      variant="outlined"
      control={control}
      name={name}
      label={t(name)}
      options={enumToOptions(type).map((option) => ({
        ...option,
        label: t(`employeeMessages.${name}.${option.label}`)
      }))}
      disableClearable
    />
  );

  return (
    <Header>
      <FilterContainer display="flex">
        {renderAutoComplete('type', TaskDefinitionGroup)}

        <Tooltip
          title={
            <div
              dangerouslySetInnerHTML={{ __html: t('employeeMessages.info') }}
            />
          }
          arrow
          placement="bottom"
        >
          <Icon type="circle-info" fontSize="small" />
        </Tooltip>

        {renderAutoComplete('status', TaskDefinitionStatus)}

        <Tooltip title={t('employeeMessages.sync')} arrow placement="bottom">
          <SyncButton
            variant="contained"
            color="primary"
            onClick={refetchMessages}
          >
            <Icon type="arrows-rotate" fontSize="small" />
          </SyncButton>
        </Tooltip>

        {!isEmployeeCard && (
          <>
            {enableBulkActions && (
              <Button
                onClick={allMessagesSelected ? onDeselectAll : onSelectAll}
                variant="outlined"
                color="primary"
              >
                {allMessagesSelected
                  ? t('employeeMessages.deselectAll')
                  : t('employeeMessages.selectAll')}
              </Button>
            )}

            {selectedMessages.length > 0 && (
              <Button
                onClick={handleAction}
                variant="contained"
                color="primary"
              >
                <Icon
                  type="check-double"
                  fontSize="small"
                  sx={{
                    color: (theme) => theme.palette.common.white,
                    marginRight: (theme) => theme.spacing(2)
                  }}
                />

                {t('employeeMessages.actions.seen')}
              </Button>
            )}
          </>
        )}
      </FilterContainer>

      <Pagination
        count={pageCount}
        onChange={(_, pageNr: number) => onPageChange(pageNr)}
      />
    </Header>
  );
};

export default Filters;
